import * as d3 from 'd3'
import * as React from 'react'

interface ChartGridProps {
  dimensions: { width: number; height: number }
  xScale: any
  yScale: any
  margin: { left: number; right: number; top: number; bottom: number }
  gridLines?: boolean
  xTickFormatter?: (value: any) => string
  yTickFormatter?: (value: any) => string
  fontSize?: number
  rotateXLabels?: boolean
  truncateXLabels?: boolean
  maxLabelLength?: number
  tickSpacing?: number
  // Explicit x-axis tick positions for non-band scales, so a sparse series does not repeat a label.
  xTickValues?: any[]
}

export const ChartGrid = React.memo<ChartGridProps>(
  ({
    dimensions,
    xScale,
    yScale,
    margin,
    gridLines = true,
    xTickFormatter,
    yTickFormatter,
    fontSize = 10,
    rotateXLabels = false,
    truncateXLabels = false,
    maxLabelLength = 12,
    tickSpacing,
    xTickValues,
  }) => {
    const gx = React.useRef<SVGGElement>(null)
    const gy = React.useRef<SVGGElement>(null)

    React.useEffect(() => {
      if (!xScale || !yScale || !dimensions.width) return

      const isBand = !!xScale.bandwidth

      const xAxis = d3.axisBottom(xScale).tickSizeOuter(0)

      if (isBand) {
        const domain = xScale.domain()
        const spacing = tickSpacing ?? 60
        const maxTicks = Math.floor(dimensions.width / spacing)
        if (domain.length > maxTicks) {
          const step = Math.ceil(domain.length / maxTicks)
          const tickValues = domain.filter(
            (_: any, i: number) => i % step === 0,
          )
          xAxis.tickValues(tickValues)
        }
      } else if (xTickValues && xTickValues.length > 0) {
        xAxis.tickValues(xTickValues)
      } else {
        const estimatedLabelWidth = 70
        const maxTicks = Math.floor(dimensions.width / estimatedLabelWidth)
        xAxis.ticks(Math.min(maxTicks, 8))
      }

      if (xTickFormatter) {
        xAxis.tickFormat(xTickFormatter)
      }

      const yAxis = d3.axisLeft(yScale).ticks(dimensions.height / 40)

      if (yTickFormatter) {
        yAxis.tickFormat(yTickFormatter)
      }

      const gxGroup = d3.select(gx.current!)
      gxGroup.call(xAxis)
      gxGroup.select('.domain').remove()
      gxGroup.selectAll('.tick line').attr('stroke-opacity', 0.1)

      const xLabels = gxGroup
        .selectAll('.tick text')
        .attr('fill', 'currentColor')
        .style('color', 'var(--color-muted-foreground, #4b5563)')
        .attr('font-size', `${fontSize}px`)

      if (truncateXLabels) {
        xLabels.text(function () {
          const text = d3.select(this).text()
          if (text.length > maxLabelLength) {
            return `${text.substring(0, maxLabelLength)}...`
          }
          return text
        })
      }

      if (rotateXLabels) {
        xLabels
          .attr('text-anchor', 'end')
          .attr('transform', 'rotate(-45)')
          .attr('dx', '-0.5em')
          .attr('dy', '0.15em')
      }

      const gyGroup = d3.select(gy.current!)
      gyGroup.call(yAxis)
      gyGroup.select('.domain').remove()

      if (gridLines) {
        gyGroup
          .selectAll('.tick line')
          .attr('x2', dimensions.width - margin.left - margin.right)
          .attr('stroke-opacity', 0.1)
      }

      gyGroup
        .selectAll('.tick text')
        .attr('fill', 'currentColor')
        .style('color', 'var(--color-muted-foreground, #4b5563)')
        .attr('font-size', `${fontSize}px`)
    }, [
      xScale,
      yScale,
      dimensions,
      margin,
      gridLines,
      xTickFormatter,
      yTickFormatter,
      fontSize,
      rotateXLabels,
      truncateXLabels,
      maxLabelLength,
      tickSpacing,
      xTickValues,
    ])

    return (
      <>
        <g
          ref={gx}
          transform={`translate(0,${dimensions.height - margin.bottom})`}
        />
        <g ref={gy} transform={`translate(${margin.left},0)`} />
      </>
    )
  },
)
